import type {
  ApiErrorBody,
  JoinContextResponse,
  JoinInviteResponse,
  JoinResponse,
} from '@hiredude/meet-shared';

const BASE = (import.meta.env.VITE_MEET_API_URL ?? 'http://localhost:4000').replace(/\/+$/, '');

export class MeetApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'MeetApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...init?.headers },
    });
  } catch {
    throw new MeetApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (!response.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      // Non-JSON error body; fall through to the generic message.
    }
    throw new MeetApiError(
      response.status,
      body?.error.code ?? 'http_error',
      body?.error.message ?? `Request failed with status ${response.status}`,
    );
  }

  return (await response.json()) as T;
}

export function fetchJoinContext(code: string, key: string): Promise<JoinContextResponse> {
  return request(`/v1/join/${encodeURIComponent(code)}?k=${encodeURIComponent(key)}`);
}

/** Host only. Returns the guest's link, never the caller's own. */
export function fetchInvite(code: string, key: string): Promise<JoinInviteResponse> {
  return request(`/v1/join/${encodeURIComponent(code)}/invite?k=${encodeURIComponent(key)}`);
}

export function exchangeForToken(
  code: string,
  key: string,
  name?: string,
): Promise<JoinResponse> {
  return request('/v1/join', {
    method: 'POST',
    body: JSON.stringify({ code, key, name }),
  });
}

export function hostAction(
  code: string,
  key: string,
  body: { identity: string; action: 'mute' | 'remove'; kind?: 'audio' | 'video' | 'all' },
): Promise<{ ok: true }> {
  return request(`/v1/join/${encodeURIComponent(code)}/host-action?k=${encodeURIComponent(key)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function endMeeting(code: string, key: string): Promise<{ ok: true }> {
  return request(`/v1/join/${encodeURIComponent(code)}/end?k=${encodeURIComponent(key)}`, {
    method: 'POST',
  });
}

export type PersistedChatMessage = {
  id: string;
  identity: string;
  role: 'host' | 'guest' | string;
  name: string;
  text: string;
  at: string;
  sourceId?: string;
};

export function fetchChatHistory(
  code: string,
  key: string,
): Promise<{ messages: PersistedChatMessage[] }> {
  return request(`/v1/join/${encodeURIComponent(code)}/chat?k=${encodeURIComponent(key)}`);
}

/** For the recording page in egress Chrome, which has only its LiveKit token. */
export function fetchRecorderChatHistory(
  recorderToken: string,
): Promise<{ messages: PersistedChatMessage[] }> {
  return request('/v1/egress/chat', {
    headers: { authorization: `Bearer ${recorderToken}` },
  });
}

export function persistChatMessage(
  code: string,
  key: string,
  body: {
    text: string;
    at?: string;
    sourceId?: string;
    fromIdentity?: string;
    fromName?: string;
  },
): Promise<{ id?: string; ok?: true }> {
  return request(`/v1/join/${encodeURIComponent(code)}/chat?k=${encodeURIComponent(key)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
